import { useState, useRef, useEffect } from "react";
import { Bell, CheckCheck, BarChart3, HelpCircle } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { usePortalCompany } from "@/hooks/usePortalCompany";
import { Link } from "react-router-dom";

type Notification = {
  id: string;
  type: string;
  title: string;
  message: string | null;
  related_id: string | null;
  is_read: boolean;
  created_at: string;
};

const typeConfig: Record<string, { icon: typeof Bell; color: string; href: string }> = {
  project_update: { icon: BarChart3, color: "text-teal", href: "/portal/progress" },
  question_answered: { icon: HelpCircle, color: "text-lime", href: "/portal/questions" },
};

const NotificationBell = () => {
  const { companyId } = usePortalCompany();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const { data: notifications = [] } = useQuery<Notification[]>({
    queryKey: ["notifications", companyId],
    queryFn: async () => {
      const { data } = await supabase
        .from("notifications")
        .select("*")
        .eq("company_id", companyId!)
        .order("created_at", { ascending: false })
        .limit(20);
      return (data ?? []) as Notification[];
    },
    enabled: !!companyId,
    refetchInterval: 30000, // poll every 30 seconds
  });

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const markAllRead = useMutation({
    mutationFn: async () => {
      const unreadIds = notifications.filter((n) => !n.is_read).map((n) => n.id);
      if (unreadIds.length === 0) return;
      await supabase
        .from("notifications")
        .update({ is_read: true })
        .in("id", unreadIds);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const markOneRead = useMutation({
    mutationFn: async (id: string) => {
      await supabase.from("notifications").update({ is_read: true }).eq("id", id);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 rounded-xl text-primary-foreground/40 hover:text-teal hover:bg-teal/5 transition-all"
        aria-label="Notifications"
      >
        <Bell className="w-4 h-4" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 w-2 h-2 bg-teal rounded-full" />
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-navy-deep border border-primary-foreground/10 rounded-2xl shadow-2xl shadow-black/40 z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-primary-foreground/5">
            <div className="flex items-center gap-2">
              <span className="text-sm font-display text-primary-foreground">Notifications</span>
              {unreadCount > 0 && (
                <span className="px-2 py-0.5 bg-teal/15 text-teal text-[10px] font-bold rounded-full font-body">
                  {unreadCount} new
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={() => markAllRead.mutate()}
                className="flex items-center gap-1 text-[10px] text-primary-foreground/50 hover:text-teal transition-colors font-body"
              >
                <CheckCheck className="w-3 h-3" />
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <Bell className="w-8 h-8 text-primary-foreground/10 mx-auto mb-2" />
                <p className="text-xs text-primary-foreground/50 font-body">No notifications yet</p>
              </div>
            ) : (
              notifications.map((n) => {
                const cfg = typeConfig[n.type] ?? typeConfig.project_update;
                const Icon = cfg.icon;
                return (
                  <Link
                    key={n.id}
                    to={cfg.href}
                    onClick={() => { markOneRead.mutate(n.id); setOpen(false); }}
                    className={`flex items-start gap-3 px-4 py-3 hover:bg-primary-foreground/3 transition-all border-b border-primary-foreground/5 last:border-0 ${
                      !n.is_read ? "bg-teal/3" : ""
                    }`}
                  >
                    <div className={`w-7 h-7 rounded-lg bg-primary-foreground/5 flex items-center justify-center flex-shrink-0 mt-0.5 ${cfg.color}`}>
                      <Icon className="w-3.5 h-3.5" />
                    </div>
                    <div className="flex-1 min-w-0">
                       <p className={`text-xs font-body font-medium leading-snug ${n.is_read ? "text-primary-foreground/70" : "text-primary-foreground/90"}`}>
                        {n.title}
                      </p>
                      {n.message && (
                        <p className="text-[11px] text-primary-foreground/50 font-body mt-0.5 line-clamp-2">{n.message}</p>
                      )}
                      <p className="text-[10px] text-primary-foreground/40 font-body mt-1">
                        {new Date(n.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                      </p>
                    </div>
                    {!n.is_read && (
                      <div className="w-1.5 h-1.5 rounded-full bg-teal mt-2 flex-shrink-0" />
                    )}
                  </Link>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
